import classNames from 'classnames'
import type { InputProps } from 'element-plus'
import { ElInput } from 'element-plus'
import { DefineComponentProps, formatAddress, omit } from 'utils'
import './InputView.scss'

interface InputViewProps extends Partial<InputProps> {
  onFocus?: (evt: FocusEvent) => void
  onBlur?: (evt: FocusEvent) => void
  onChange?: (value: string) => void
  onInput?: (value: string, originValue: string) => void
  /** 是否数字 */
  isNumber?: boolean
  /** 是否浮点数 */
  isFloatNumber?: boolean
  /** 是否是地址 */
  isAddress?: boolean
  /** 圆角 */
  radius?: number
  /** 标题 */
  label?: string
  /** 高度 */
  height?: number
  /** 支持的小数位数 */
  maxDecimalLength?: number
  /** 最大值 */
  max?: number | string
  /** 支持的整数位数 */
  maxIntegerLength?: number
  /** 是否允许输入空格 */
  allowSpace?: boolean
  /** 跟随字符 */
  followChar?: string
  /** 是否支持负数 */
  allowNegative?: boolean
}

export type InputViewExpose = {
  focus: () => void
}

export const InputView = defineComponent<InputViewProps>({
  props: DefineComponentProps<InputViewProps>([
    'onInput',
    'onChange',
    'isNumber',
    'isFloatNumber',
    'onBlur',
    'onFocus',
    'placeholder',
    'suffixIcon',
    'prefixIcon',
    'formatter',
    'parser',
    'maxlength',
    'ariaLabel',
    'label',
    'autocomplete',
    'autofocus',
    'autosize',
    'disabled',
    'form',
    'inputStyle',
    'resize',
    'clearable',
    'showPassword',
    'showWordLimit',
    'modelValue',
    'type',
    'radius',
    'isAddress',
    'height',
    'maxDecimalLength',
    'max',
    'maxIntegerLength',
    'allowSpace',
    'allowNegative',
  ]),
  emits: ['update:modelValue'],
  setup(props, { slots, emit, expose }) {
    const { isNumber, isAddress, allowSpace = true, allowNegative = false, label = '' } = props
    const inputValue = ref(props.modelValue)
    let originValue = inputValue.value
    const inputRef = ref<HTMLInputElement>()

    const isFloatNumber = computed(() => {
      return props.isFloatNumber
    })

    const maxDecimalLength = computed(() => {
      return props.maxDecimalLength || 8
    })

    const maxIntegerLength = computed(() => {
      return props.maxIntegerLength || 18
    })

    const handleMax = (value: string) => {
      return value.slice(0, maxIntegerLength.value)
    }

    /** 处理只有一个负号 */
    const handleOnlyOneNegative = (value: string) => {
      if (value.split('-').length > 2) {
        const negativeIndex = value.lastIndexOf('-')
        return value.slice(0, negativeIndex) + value.slice(negativeIndex + 1)
      }
      return value
    }

    /** 处理只有一个小数点 */
    const handleOnlyOneDot = (value: string) => {
      if (value.split('.').length > 2) {
        const dotIndex = value.lastIndexOf('.')
        return value.slice(0, dotIndex) + value.slice(dotIndex + 1)
      }
      return value
    }

    const handleFloatNumber = (value: string) => {
      if (allowNegative) {
        value = value.replace(/[^0-9.-]/g, '')
        value = handleOnlyOneNegative(value)
      } else {
        value = value.replace(/[^0-9.]/g, '')
      }
      if (value.startsWith('0') && value[1] === '0') {
        return '0'
      }
      if (value.includes('.')) {
        value = handleOnlyOneDot(value)
        const [_integerPart, decimalPart] = value.split('.')
        const integerPart = handleMax(_integerPart)
        if (decimalPart.length > maxDecimalLength.value) {
          value = `${integerPart}.${decimalPart.slice(0, maxDecimalLength.value)}`
        }
      } else {
        value = handleMax(value)
      }

      return value
    }

    const handleNumber = (value: string) => {
      value = value.trim()
      if (value.startsWith('0') && value[1] === '0') {
        return '0'
      }

      if (allowNegative) {
        value = value.replace(/[^0-9-]/g, '')
      } else {
        value = value.replace(/[^0-9]/g, '')
      }
      value = handleMax(value)
      return value
    }

    const formatterValue = (value: string) => {
      if (!allowSpace) {
        value = value.trim()
      }
      if (props.max) {
        value = `${+value > +props.max ? props.max : value}`
      }
      if (isFloatNumber.value) {
        value = value.trim()
        return handleFloatNumber(value)
      }
      if (isNumber) {
        return handleNumber(value)
      }
      if (isAddress) {
        return formatAddress(value)
      }
      if (props.formatter) {
        return props.formatter(value, originValue)
      }
      return value
    }

    const parserValue = (value: string) => {
      if (!allowSpace) {
        value = value.trim()
      }
      if (props.max) {
        value = `${+value > +props.max ? props.max : value}`
      }
      if (isFloatNumber.value) {
        value = value.trim()
        return handleFloatNumber(value)
      }
      if (isNumber) {
        return handleNumber(value)
      }
      if (isAddress) {
        return formatAddress(value)
      }
      if (props.parser) {
        return props.parser(value)
      }
      return value
    }

    watch(
      () => props.modelValue,
      (value) => {
        inputValue.value = value
      },
    )

    expose({
      focus: () => {
        inputRef.value?.focus()
      },
    })

    return () => (
      <div
        class={classNames('input-view', {
          'has-fixed-suffix': slots.fixedSuffix,
        })}
        style={{
          ...(props.radius != null ? { '--input-view-radius': `${props.radius}px` } : {}),
          ...(props.height != null ? { '--input-view-height': `${props.height}px` } : {}),
        }}
        onInput={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onBlur={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        {label ? <div class={'input-label'}> {label}</div> : null}
        <ElInput
          {...omit(props, ['onInput', 'onBlur', 'onFocus'])}
          modelValue={inputValue.value}
          formatter={formatterValue}
          parser={parserValue}
          onInput={(e) => {
            originValue = inputValue.value as string
            emit('update:modelValue', e)
            inputValue.value = e
            props.onInput?.(e, originValue)
          }}
          onFocus={(e) => {
            e.stopPropagation()
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            e.stopPropagation()
            props.onBlur?.(e)
          }}
          showWordLimit={props.showWordLimit}
          ref={inputRef}
        >
          {{
            suffix: () => (
              <span>
                {props.showWordLimit && props.maxlength && (props.type === 'password' || props.showPassword) && (
                  <span class={'word-limit'}>
                    {`${inputValue.value || ''}`.length} / {props.maxlength}
                  </span>
                )}
                {slots.suffix?.()}
              </span>
            ),
            prefix: () => slots.prefix?.(),
          }}
        </ElInput>
        {slots.fixedSuffix && <div class={'input-view-fixed-suffix'}>{slots.fixedSuffix?.()}</div>}
      </div>
    )
  },
})
